(() => {
  if (customElements.get('evilko-live-hero')) return;
  const POLL_MS = 15000;
  const STALE_MS = 30000;
  const PATHS = { bingo: '/pages/bingo', tiles: '/pages/mahjong-match' };
  const safeImage = value => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.origin);
      const arcadeBanner = value === url.href && url.hostname === 'arcade.evilko.com' && !url.port && /^\/game-banner-media\/[a-f0-9]{48}\.(?:png|jpg|webp)$/.test(url.pathname) && !url.search && !url.hash;
      if (url.protocol !== 'https:' || url.username || url.password || !(arcadeBanner || url.origin === location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopify.com'))) return '';
      return url.href;
    } catch { return ''; }
  };
  class EvilkoLiveHero extends HTMLElement {
    connectedCallback() {
      if (this.running) return;
      try { this.config = JSON.parse(this.querySelector('[data-live-hero-config]').textContent); } catch { return; }
      this.original = this.parentElement.querySelector('[data-en-default-hero]');
      if (!this.original || !this.config.games || !this.config.copy) return;
      this.originalHidden = this.original.hidden;
      this.running = true;
      this.games = [];
      this.renderKey = '';
      this.selectedKey = '';
      this.receivedAt = 0;
      this.lastServerTime = 0;
      this.refs = Object.fromEntries(['title', 'detail', 'name', 'timer', 'timer-label', 'cta', 'media', 'mobile', 'background', 'choices', 'prize'].map(name => [name, this.querySelector(`[data-live-${name}]`)]));
      this.onChoice = event => {
        const button = event.target.closest('[data-live-choice]');
        if (!button || !this.contains(button)) return;
        this.selectedKey = button.dataset.liveChoice;
        this.tick();
      };
      this.onVisibility = () => {
        this.tick();
        if (!document.hidden) this.refresh();
      };
      this.onOffline = () => { this.games = []; this.receivedAt = 0; this.controller?.abort(); this.restore(); };
      this.onPageShow = () => { this.restore(); this.refresh(); };
      this.addEventListener('click', this.onChoice);
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('offline', this.onOffline);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('online', this.onVisibility);
      this.tickTimer = window.setInterval(() => this.tick(), 1000);
      this.pollTimer = window.setInterval(() => { if (!document.hidden) this.refresh(); }, POLL_MS);
      this.refresh();
    }
    disconnectedCallback() {
      this.running = false;
      this.controller?.abort();
      window.clearInterval(this.tickTimer);
      window.clearInterval(this.pollTimer);
      this.removeEventListener('click', this.onChoice);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('offline', this.onOffline);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('online', this.onVisibility);
      this.restore();
    }
    async refresh() {
      if (this.loading || !this.running) return;
      this.loading = true;
      const controller = new AbortController();
      this.controller = controller;
      const timeout = window.setTimeout(() => controller.abort(), 6000);
      const started = performance.now();
      try {
        const endpoint = new URL('/apps/evilko-wheel/live-games', location.origin);
        endpoint.searchParams.set('_', String(Date.now()));
        const response = await fetch(endpoint.href, { cache: 'no-store', credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('unavailable');
        const payload = await response.json();
        if (!this.running || controller.signal.aborted) return;
        const received = performance.now();
        const serverTime = payload?.serverTime;
        const responseDate = Date.parse(response.headers?.get('Date') || '');
        const age = Number(response.headers?.get('Age') || 0);
        if (!Number.isSafeInteger(serverTime) || serverTime <= 0 || !Array.isArray(payload.liveGames) || serverTime <= this.lastServerTime || age > 0 || (Number.isFinite(responseDate) && Math.abs(responseDate - serverTime) > STALE_MS)) throw new Error('stale');
        this.lastServerTime = serverTime;
        this.serverTime = serverTime + Math.min((received - started) / 2, 3000);
        this.receivedAt = received;
        this.games = payload.liveGames.filter(game => game && Object.hasOwn(PATHS, game.type) && Object.hasOwn(this.config.games, game.type) && (typeof game.id === 'string' || Number.isSafeInteger(game.id)) && Number.isSafeInteger(game.startsAt) && game.startsAt <= serverTime && ((['bingo','tiles'].includes(game.type) && game.manual === true && game.endsAt === null) || (Number.isSafeInteger(game.endsAt) && game.endsAt > serverTime && game.endsAt > game.startsAt)) && typeof game.entryOpen === 'boolean' && (game.entryOpen || ['bingo','tiles'].includes(game.type))).map(game => ({ ...game, key: `${game.type}:${game.id}` })).sort((a, b) => (a.endsAt ?? Number.MAX_SAFE_INTEGER) - (b.endsAt ?? Number.MAX_SAFE_INTEGER) || a.startsAt - b.startsAt || a.key.localeCompare(b.key));
        this.tick();
      } catch {
        if (this.running) { this.games = []; this.restore(); }
      } finally {
        window.clearTimeout(timeout);
        this.loading = false;
      }
    }
    restore() {
      this.hidden = true;
      if (this.original) this.original.hidden = this.originalHidden;
      this.renderKey = '';
    }
    tick() {
      if (!this.running || !this.receivedAt || performance.now() - this.receivedAt >= STALE_MS) { this.restore(); return; }
      const now = this.serverTime + performance.now() - this.receivedAt;
      const active = this.games.filter(game => game.startsAt <= now && ((['bingo','tiles'].includes(game.type) && game.manual === true && game.endsAt === null) || game.endsAt > now));
      if (!active.length) { this.restore(); return; }
      const selected = active.find(game => game.key === this.selectedKey) || active[0];
      this.selectedKey = selected.key;
      const renderKey = JSON.stringify([selected.key, selected.title, selected.banner, selected.mobileBanner, selected.entryOpen, selected.endsAt, active.map(game => game.key)]);
      if (renderKey !== this.renderKey) {
        this.render(selected, active);
        this.renderKey = renderKey;
      }
      const manual = ['bingo','tiles'].includes(selected.type) && selected.manual === true && selected.endsAt === null;
      if (this.refs['timer-label']) this.refs['timer-label'].textContent = manual ? this.config.copy.game_status : this.config.copy.closes_in;
      const remaining = Math.max(0, Math.ceil((selected.endsAt - now) / 1000));
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor(remaining % 3600 / 60);
      const seconds = remaining % 60;
      this.refs.timer.textContent = manual ? (selected.entryOpen ? (selected.type === 'tiles' ? this.config.copy.play_open : this.config.copy.registration_open) : this.config.copy.in_progress) : `${hours ? `${String(hours).padStart(2, '0')}:` : ''}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      this.original.hidden = true;
      this.hidden = false;
    }
    render(game, active) {
      const settings = this.config.games[game.type];
      if (!settings) { this.restore(); return; }
      this.dataset.liveType = game.type;
      this.refs.title.textContent = settings.name;
      this.refs.detail.textContent = game.type === 'bingo' && !game.entryOpen ? this.config.copy.bingo_started : settings.detail;
      this.refs.name.textContent = typeof game.title === 'string' ? game.title : '';
      this.refs.prize.hidden = !this.refs.name.textContent;
      this.refs.cta.href = PATHS[game.type];
      this.refs.cta.textContent = game.entryOpen ? settings.play : this.config.copy.view_game;
      this.renderBanner(game);
      const focusedChoice = this.refs.choices.contains(document.activeElement) ? document.activeElement.dataset.liveChoice : '';
      const buttons = active.map(item => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.liveChoice = item.key;
        button.setAttribute('aria-pressed', String(item.key === game.key));
        const duplicateType = active.filter(other => other.type === item.type).length > 1;
        button.textContent = this.config.games[item.type].name + (duplicateType && typeof item.title === 'string' ? ` · ${item.title}` : '');
        return button;
      });
      this.refs.choices.replaceChildren(...buttons);
      this.refs.choices.hidden = buttons.length < 2;
      if (focusedChoice && buttons.length > 1) buttons.find(button => button.dataset.liveChoice === focusedChoice)?.focus({ preventScroll: true });
    }
    renderBanner(settings) {
      const mobileBanner = safeImage(settings.mobileBanner);
      const banner = safeImage(settings.banner) || mobileBanner;
      const { media, mobile, background } = this.refs;
      background.onerror = null;
      mobile.removeAttribute('srcset');
      media.hidden = !banner;
      if (!banner) { background.removeAttribute('src'); return; }
      background.onerror = () => {
        if (mobile.hasAttribute('srcset') && background.currentSrc !== banner) {
          mobile.removeAttribute('srcset');
          background.src = banner;
        } else {
          background.onerror = null;
          media.hidden = true;
          background.removeAttribute('src');
        }
      };
      if (mobileBanner && mobileBanner !== banner) mobile.srcset = mobileBanner;
      background.src = banner;
    }
  }
  customElements.define('evilko-live-hero', EvilkoLiveHero);
})();

